import { Component, Input, OnDestroy, OnInit } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { HttpStatusCode } from '@angular/common/http'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { CallerService } from '../services/caller.service'
import { AccommodationActionsService } from '../services/accommodation-actions.service'
import { AccommodationType } from '../ngrx-store/app.state'
import { deletePostedPost } from '../ngrx-store/actions/corp-member.actions'
import { DeletePostComponent } from '../dialogs/delete-post/delete-post.component'
import { leavingLabel, nearness, saleLocation } from '../utils/sale.util'
import { accommodationTypeLabel, rentIntervalLabel } from '../utils/accommodation.util'

/** A house in a listing (/houses, search, the home feed, the dashboard) - laid out like the sale card. */
@Component({
    selector: 'app-feed-accommodation-content',
    templateUrl: './feed-accommodation-content.component.html',
    styleUrls: ['./feed-accommodation-content.component.css'],
})
export class FeedAccommodationContentComponent implements OnInit, OnDestroy {
    @Input() accommodation!: AccommodationType

    /** Delete on your own house - where you manage your posts (the dashboard's Post history). */
    @Input() ownerActions = false

    private savedIds = new Set<number>()
    isSaving = false

    private destroy$ = new Subject<void>()

    constructor(
        private dialog: MatDialog,
        public callerService: CallerService,
        public houseActions: AccommodationActionsService,
    ) {}

    ngOnInit(): void {
        this.houseActions.savedIds().pipe(takeUntil(this.destroy$)).subscribe((ids) => (this.savedIds = ids))
    }

    ngOnDestroy(): void {
        this.destroy$.next()
        this.destroy$.complete()
    }

    get saved(): boolean {
        return this.savedIds.has(this.accommodation.id)
    }

    toggleSave(): void {
        this.isSaving = true
        this.houseActions.toggleSave(this.accommodation.id, this.saved).subscribe(() => (this.isSaving = false))
    }

    get isOwn(): boolean {
        return this.callerService.isLoggedIn && Boolean(this.accommodation?.corp_member_id) &&
            this.accommodation.corp_member_id === this.callerService.corpMember?.id
    }

    get phoneNumbers(): Array<{ number: string; label: string }> {
        return this.accommodation?.contactPhoneNumbers ?? []
    }

    /** Only to logged-in corp members, as before. */
    get posterName(): string | undefined {
        if (!this.callerService.isLoggedIn) return undefined
        return this.accommodation.CorpMember?.nickname ?? this.accommodation.CorpMember?.first_name
    }

    get typeLabel(): string {
        return accommodationTypeLabel(this.accommodation?.accommodation_type)
    }

    /** "Ikeja, Lagos" - as sale cards show it. */
    get location(): string {
        return saleLocation(this.accommodation?.CorpMember?._location)
    }

    get isFree(): boolean {
        return this.accommodation?.rent_interval === 'free'
    }

    get seekingRoommate(): boolean {
        return this.accommodation?.status === 'seeking_roommate'
    }

    get rentIntervalLabel(): string {
        return rentIntervalLabel(this.accommodation?.rent_interval)
    }

    /** Same LGA as you. */
    get isNearYou(): boolean {
        return this.callerService.isLoggedIn && nearness(this.accommodation?.CorpMember?._location, this.callerService.corpMember?._location) === 2
    }

    get leaving(): string | undefined {
        return leavingLabel(this.accommodation?.CorpMember?.service_year_end_date)
    }

    confirmDeletePost(): void {
        this.dialog
            .open(DeletePostComponent, { maxWidth: '360px', data: { accommodation: this.accommodation } })
            .afterClosed()
            .subscribe((res) => {
                if (res === 'delete') this.deletePost()
            })
    }

    private deletePost(): void {
        if (this.accommodation?.corp_member_id !== this.callerService.corpMember.id) {
            this.callerService.showNotification('Accommodation is not yours to delete')
            return
        }
        this.callerService.deletePostedItem({ id: this.accommodation.id, type: 'accommodation' }).subscribe({
            next: (res) => {
                if (res.status === HttpStatusCode.Ok) {
                    this.callerService.store.dispatch(deletePostedPost({ id: `accommodation_${this.accommodation.id}` }))
                    this.callerService.offerUndoDelete({ id: this.accommodation.id, type: 'accommodation' }, 'Accommodation deleted.')
                }
            },
            error: (err) => this.callerService.showNotification(err?.error?.message ?? 'Failed to delete accommodation'),
        })
    }
}
